import { ElementRef, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UnreadMessageCount, UnreadMessageCountResponse } from '../models/user';
import { set } from 'dot-object';
import moment, { Moment } from 'moment';
import _ from 'lodash';
import { ElementLockType, ScrollLockService } from '../scroll-lock/services/scroll-lock.service';

export enum PanelType {
  Message = 'message-panel',
  Notification = 'notification-panel',
  Profile = 'profile-panel',
  DataFilter = 'data-filter',
  Contacts = 'contacts',
  Services = 'services',
  Timeline = 'timeline',
  EventsTimeline = 'events-timeline',
  MainMenu = 'main-menu',
  MainNavigationMenu = 'main-navigation-menu',
  ArrangementDetail = 'arrangement-detail',
  ServiceProviderDetail = 'service-provider-detail',
  ProductDetail = 'product-detail',
  ArrangementNote = 'arrangement-note',
  ArrangementTask = 'arrangement-task',
  ArrangementTasks = 'arrangement-tasks',
  SendEmail = 'send-email',
  ArrangementMenu = 'arrangement-menu',
  EmailTrails = 'email-trails',
  DocumentLibrary = 'document-library',
  ArrangementTransfer = 'arrangement-transfer',
  ArrangementTransfers = 'arrangement-transfers',
  ArrangementEvent = 'arrangement-event',
  ArrangementContact = 'arrangement-contact',
  ArrangementInvoiceHistories = 'arrangement-invoice-histories',
  GroupedPanel = 'grouped-panel',
}

export interface SiteServicePanel {
  isOpen: boolean;
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export class SiteService {
  preventScroll$: Observable<boolean>;

  messagePanel$: Observable<boolean>;
  notificationPanel$: Observable<boolean>;
  profilePanel$: Observable<boolean>;
  dataFilterPanel$: Observable<SiteServicePanel>;
  contactPanel$: Observable<SiteServicePanel>;
  servicePanel$: Observable<SiteServicePanel>;
  timelinePanel$: Observable<SiteServicePanel>;
  eventsTimelinePanel$: Observable<SiteServicePanel>;
  arrangementDetailPanel$: Observable<SiteServicePanel>;
  serviceProviderDetailPanel$: Observable<SiteServicePanel>;
  productDetailPanel$: Observable<SiteServicePanel>;
  arrangementNotePanel$: Observable<SiteServicePanel>;
  arrangementTaskPanel$: Observable<SiteServicePanel>;
  arrangementTasksPanel$: Observable<SiteServicePanel>;
  sendEmailPanel$: Observable<SiteServicePanel>;
  mainMenu$: Observable<boolean>;
  mainNavigationMenu$: Observable<boolean>;
  arrangementMenuPanel$: Observable<boolean>;
  emailTrailsPanel$: Observable<SiteServicePanel>;
  documentLibraryPanel$: Observable<SiteServicePanel>;
  invoiceHistoriesPanel$: Observable<SiteServicePanel>;
  groupedPanel$: Observable<SiteServicePanel>;
  blockUi$: Observable<boolean>;
  criticalDataReady$: Observable<boolean>;

  arrangementContactPanel$: Observable<SiteServicePanel>;
  arrangementEventPanel$: Observable<SiteServicePanel>;
  arrangementTransferPanel$: Observable<SiteServicePanel>;
  arrangementTransfersPanel$: Observable<SiteServicePanel>;

  unreadMessageCount$: Observable<UnreadMessageCount>;

  allPanels: PanelType[];
  sitePanels: PanelType[];
  arrangementPanels: PanelType[];
  invoicePanels: PanelType[];

  private arrangementDetailOpenSource: BehaviorSubject<boolean>;
  public arrangementDetailOpen$: Observable<boolean>;
  private arrangementBlockedSource: BehaviorSubject<boolean>;
  public arrangementBlocked$: Observable<boolean>;

  private preventScrollSource: BehaviorSubject<boolean>;

  private messagePanelSource: BehaviorSubject<boolean>;
  private notificationPanelSource: BehaviorSubject<boolean>;
  private profilePanelSource: BehaviorSubject<boolean>;
  private dataFilterPanelSource: BehaviorSubject<SiteServicePanel>;
  private contactPanelSource: BehaviorSubject<SiteServicePanel>;
  private servicePanelSource: BehaviorSubject<SiteServicePanel>;
  private timelinePanelSource: BehaviorSubject<SiteServicePanel>;
  private eventsTimelinePanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementDetailPanelSource: BehaviorSubject<SiteServicePanel>;
  private serviceProviderDetailPanelSource: BehaviorSubject<SiteServicePanel>;
  private productDetailPanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementNotePanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementTaskPanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementTasksPanelSource: BehaviorSubject<SiteServicePanel>;
  private sendEmailPanelSource: BehaviorSubject<SiteServicePanel>;
  private mainMenuSource: BehaviorSubject<boolean>;
  private mainNavigationMenuSource: BehaviorSubject<boolean>;
  private arrangementMenuPanelSource: BehaviorSubject<boolean>;
  private emailTrailsPanelSource: BehaviorSubject<SiteServicePanel>;
  private documentLibraryPanelSource: BehaviorSubject<SiteServicePanel>;
  private invoiceHistoriesPanelSource: BehaviorSubject<SiteServicePanel>;
  private groupedPanelSource: BehaviorSubject<SiteServicePanel>;
  private blockUiSource: BehaviorSubject<boolean>;
  private criticalDataReadySource: BehaviorSubject<boolean>;

  private arrangementContactPanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementEventPanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementTransferPanelSource: BehaviorSubject<SiteServicePanel>;
  private arrangementTransfersPanelSource: BehaviorSubject<SiteServicePanel>;

  private unreadMessageCountSource!: BehaviorSubject<UnreadMessageCount>;

  get mainMenuAsValue(): boolean {
    return this.mainMenuSource.value;
  }

  constructor(
    private scrollLockService: ScrollLockService,
    private rendererFactory: RendererFactory2
  ) {
    // One-time cleanup of the removed subscription-log store: sessions carried up to
    // 10,000 stale debug entries (including deceased names) in localStorage.
    try {
      localStorage.removeItem('subscriptionLog');
    } catch {
      // localStorage unavailable - nothing to clean.
    }

    this.preventScrollSource = new BehaviorSubject<boolean>(false);
    this.preventScroll$ = this.preventScrollSource.asObservable();
    this.arrangementDetailOpenSource = new BehaviorSubject<boolean>(false);
    this.arrangementDetailOpen$ = this.arrangementDetailOpenSource.asObservable();
    this.arrangementBlockedSource = new BehaviorSubject<boolean>(false);
    this.arrangementBlocked$ = this.arrangementBlockedSource.asObservable();

    this.messagePanelSource = new BehaviorSubject<boolean>(false);
    this.messagePanel$ = this.messagePanelSource.asObservable();

    this.notificationPanelSource = new BehaviorSubject<boolean>(false);
    this.notificationPanel$ = this.notificationPanelSource.asObservable();

    this.profilePanelSource = new BehaviorSubject<boolean>(false);
    this.profilePanel$ = this.profilePanelSource.asObservable();

    this.dataFilterPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.dataFilterPanel$ = this.dataFilterPanelSource.asObservable();

    this.contactPanelSource = new BehaviorSubject<SiteServicePanel>({ isOpen: false, data: null });
    this.contactPanel$ = this.contactPanelSource.asObservable();

    this.servicePanelSource = new BehaviorSubject<SiteServicePanel>({ isOpen: false, data: null });
    this.servicePanel$ = this.servicePanelSource.asObservable();

    this.timelinePanelSource = new BehaviorSubject<SiteServicePanel>({ isOpen: false, data: null });
    this.timelinePanel$ = this.timelinePanelSource.asObservable();

    this.eventsTimelinePanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.eventsTimelinePanel$ = this.eventsTimelinePanelSource.asObservable();

    this.arrangementDetailPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementDetailPanel$ = this.arrangementDetailPanelSource.asObservable();

    this.serviceProviderDetailPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.serviceProviderDetailPanel$ = this.serviceProviderDetailPanelSource.asObservable();

    this.productDetailPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.productDetailPanel$ = this.productDetailPanelSource.asObservable();

    this.arrangementNotePanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementNotePanel$ = this.arrangementNotePanelSource.asObservable();

    this.arrangementTaskPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementTaskPanel$ = this.arrangementTaskPanelSource.asObservable();

    this.arrangementTasksPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementTasksPanel$ = this.arrangementTasksPanelSource.asObservable();

    this.sendEmailPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.sendEmailPanel$ = this.sendEmailPanelSource.asObservable();

    this.mainMenuSource = new BehaviorSubject<boolean>(false);
    this.mainMenu$ = this.mainMenuSource.asObservable();

    this.mainNavigationMenuSource = new BehaviorSubject<boolean>(false);
    this.mainNavigationMenu$ = this.mainNavigationMenuSource.asObservable();

    this.arrangementMenuPanelSource = new BehaviorSubject<boolean>(false);
    this.arrangementMenuPanel$ = this.arrangementMenuPanelSource.asObservable();

    this.emailTrailsPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.emailTrailsPanel$ = this.emailTrailsPanelSource.asObservable();

    this.documentLibraryPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.documentLibraryPanel$ = this.documentLibraryPanelSource.asObservable();

    this.invoiceHistoriesPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.invoiceHistoriesPanel$ = this.invoiceHistoriesPanelSource.asObservable();

    this.groupedPanelSource = new BehaviorSubject<SiteServicePanel>({ isOpen: false, data: null });
    this.groupedPanel$ = this.groupedPanelSource.asObservable();

    this.blockUiSource = new BehaviorSubject<boolean>(true);
    this.blockUi$ = this.blockUiSource.asObservable();

    this.criticalDataReadySource = new BehaviorSubject<boolean>(false);
    this.criticalDataReady$ = this.criticalDataReadySource.asObservable();

    this.unreadMessageCountSource = new BehaviorSubject<UnreadMessageCount>({
      totalCount: 0,
      counts: {},
    });
    this.unreadMessageCount$ = this.unreadMessageCountSource.asObservable();

    this.arrangementContactPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementContactPanel$ = this.arrangementContactPanelSource.asObservable();

    this.arrangementEventPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementEventPanel$ = this.arrangementEventPanelSource.asObservable();

    this.arrangementTransferPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementTransferPanel$ = this.arrangementTransferPanelSource.asObservable();

    this.arrangementTransfersPanelSource = new BehaviorSubject<SiteServicePanel>({
      isOpen: false,
      data: null,
    });
    this.arrangementTransfersPanel$ = this.arrangementTransfersPanelSource.asObservable();

    this.allPanels = [
      PanelType.Message,
      PanelType.Notification,
      PanelType.Profile,
      PanelType.DataFilter,
      PanelType.Contacts,
      PanelType.Timeline,
      PanelType.EventsTimeline,
      PanelType.MainMenu,
      PanelType.ArrangementDetail,
      PanelType.ServiceProviderDetail,
      PanelType.ProductDetail,
      PanelType.ArrangementNote,
      PanelType.ArrangementTask,
      PanelType.ArrangementTasks,
      PanelType.SendEmail,
      PanelType.EmailTrails,
      PanelType.DocumentLibrary,
      PanelType.ArrangementContact,
      PanelType.ArrangementEvent,
      PanelType.ArrangementTransfer,
    ];

    this.sitePanels = [
      PanelType.DataFilter,
      PanelType.Contacts,
      PanelType.Timeline,
      PanelType.EventsTimeline,
      PanelType.MainMenu,
      PanelType.ArrangementDetail,
      PanelType.ServiceProviderDetail,
      PanelType.ArrangementTasks,
      PanelType.EmailTrails,
      PanelType.DocumentLibrary,
      // #163: these two can open directly from the dashboard's arrangement list, where the
      // page behind them is the document scroller — bodyLock (overflow-only) freezes it.
      // Depth-counted, so stacking over an already-locked ArrangementDetail stays balanced.
      PanelType.ArrangementInvoiceHistories,
      PanelType.SendEmail,
    ];

    this.arrangementPanels = [
      PanelType.ProductDetail,
      PanelType.ArrangementNote,
      PanelType.ArrangementTask,
      PanelType.ArrangementTasks,
      PanelType.ArrangementContact,
      PanelType.ArrangementEvent,
      PanelType.ArrangementTransfer,
      PanelType.ArrangementTransfers,
      PanelType.Contacts,
      PanelType.GroupedPanel,
      PanelType.ArrangementInvoiceHistories,
      // #163: the email panel must also freeze the arrangement behind it — it can open
      // directly over the arrangement (cost summary, contacts, document library), not
      // only over Manage Invoices. The lock is depth-counted, so the invoice flow
      // (arrangement locked once by the invoice panel, again by this) stays balanced.
      PanelType.SendEmail,
    ];

    this.invoicePanels = [PanelType.SendEmail];
  }

  closeArrangementPanels(): void {
    this.arrangementPanels.forEach((panel) => this.closePanel(panel));
  }

  closeSitePanels(): void {
    this.sitePanels.forEach((panel) => this.closePanel(panel));
  }

  closeAllPanels(): void {
    this.allPanels.forEach((panel) => this.closePanel(panel));
  }

  blockUi() {
    if (this.blockUiSource.getValue() !== true) {
      this.blockUiSource.next(true);

      const element: any = document.querySelector('#block-conductor-ui');

      if (element) {
        element.style.display = 'flex';
        element.style.opacity = 1;
      }
    }
  }

  unBlockUi() {
    if (this.blockUiSource.getValue() !== false) {
      this.blockUiSource.next(false);

      const element: any = document.querySelector('#block-conductor-ui');

      if (element) {
        element.style.opacity = 0;

        setTimeout(() => {
          element.style.display = 'none';
          // NOTE: do not write a background here. index.html owns the blocker's
          // colour (.block-ui, and html.app-dark .block-ui = #121212). An INLINE
          // style outranks the stylesheet permanently, so restoring a hardcoded
          // white here made every overlay after the first one flash near-white
          // over the dark UI. blockUi() never sets a background, so there was
          // nothing to restore.

          const pElement = document.querySelector('#block-conductor-ui p');

          if (pElement) {
            pElement.textContent = 'Processing. Please wait...';
          }
        }, 500);
      }
    }
  }

  criticalDataReady() {
    if (this.criticalDataReadySource.getValue() !== true) {
      this.criticalDataReadySource.next(true);
    }
  }

  openPanel(panel: PanelType, data?: any, preventScroll: boolean = true) {
    // this.preventScrollSource.next(preventScroll);
    const wasArrangementDetailOpen = this.arrangementDetailOpenSource.getValue();
    let didOpen = false;
    // MainMenu stays in sitePanels (so closeSitePanels() still closes it on navigation)
    // but is EXEMPT from the body lock. bodyLock sets overflow:hidden on the root, and a
    // position:sticky header inside an overflow-hidden root stops sticking on iPad WebKit
    // - it snaps back to its flow position at the document top, which is why the page
    // header vanished whenever the menu was opened on a scrolled list. The menu does not
    // need the document frozen: its backdrop covers the page and is touch-action:none, so
    // touches cannot start a document pan anyway (main-navigation.component.scss). The
    // exemption must be IDENTICAL in closePanel's shouldUnlockBody or the lock depth
    // counter strands.
    const shouldLockBody =
      this.sitePanels.includes(panel) && panel !== PanelType.MainMenu;
    const shouldLockArrangement = this.arrangementPanels.includes(panel);
    const shouldLockInvoice = this.invoicePanels.includes(panel);

    if (panel === PanelType.Message) {
      if (this.messagePanelSource.getValue() !== true) {
        this.messagePanelSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.Notification) {
      if (this.notificationPanelSource.getValue() !== true) {
        this.notificationPanelSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.Profile) {
      if (this.profilePanelSource.getValue() !== true) {
        this.profilePanelSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.DataFilter) {
      if (this.dataFilterPanelSource.getValue().isOpen !== true) {
        this.dataFilterPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.Contacts) {
      if (this.contactPanelSource.getValue().isOpen !== true) {
        this.contactPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.Services) {
      if (this.servicePanelSource.getValue().isOpen !== true) {
        this.servicePanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.Timeline) {
      if (this.timelinePanelSource.getValue().isOpen !== true) {
        this.timelinePanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.EventsTimeline) {
      if (this.eventsTimelinePanelSource.getValue().isOpen !== true) {
        this.eventsTimelinePanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementDetail) {
      if (this.arrangementDetailPanelSource.getValue().isOpen !== true) {
        this.arrangementDetailPanelSource.next({ isOpen: true, data });
        didOpen = true;
        this.arrangementDetailOpenSource.next(true);
        this.arrangementBlockedSource.next(false);
      }
    } else if (panel === PanelType.ServiceProviderDetail) {
      if (this.serviceProviderDetailPanelSource.getValue().isOpen !== true) {
        this.serviceProviderDetailPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ProductDetail) {
      if (this.productDetailPanelSource.getValue().isOpen !== true) {
        this.productDetailPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementNote) {
      if (this.arrangementNotePanelSource.getValue().isOpen !== true) {
        this.arrangementNotePanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementTask) {
      if (this.arrangementTaskPanelSource.getValue().isOpen !== true) {
        this.arrangementTaskPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementTasks) {
      if (this.arrangementTasksPanelSource.getValue().isOpen !== true) {
        this.arrangementTasksPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.SendEmail) {
      if (this.sendEmailPanelSource.getValue().isOpen !== true) {
        this.sendEmailPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.MainMenu) {
      if (this.mainMenuSource.getValue() !== true) {
        this.mainMenuSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.MainNavigationMenu) {
      if (this.mainNavigationMenuSource.getValue() !== true) {
        this.mainNavigationMenuSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementMenu) {
      if (this.arrangementMenuPanelSource.getValue() !== true) {
        this.arrangementMenuPanelSource.next(true);
        didOpen = true;
      }
    } else if (panel === PanelType.EmailTrails) {
      if (this.emailTrailsPanelSource.getValue().isOpen !== true) {
        this.emailTrailsPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.DocumentLibrary) {
      if (this.documentLibraryPanelSource.getValue().isOpen !== true) {
        this.documentLibraryPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementContact) {
      if (this.arrangementContactPanelSource.getValue().isOpen !== true) {
        this.arrangementContactPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementEvent) {
      if (this.arrangementEventPanelSource.getValue().isOpen !== true) {
        this.arrangementEventPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementTransfer) {
      if (this.arrangementTransferPanelSource.getValue().isOpen !== true) {
        this.arrangementTransferPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementTransfers) {
      if (this.arrangementTransfersPanelSource.getValue().isOpen !== true) {
        this.arrangementTransfersPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.ArrangementInvoiceHistories) {
      if (this.invoiceHistoriesPanelSource.getValue().isOpen !== true) {
        this.invoiceHistoriesPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else if (panel === PanelType.GroupedPanel) {
      if (this.groupedPanelSource.getValue().isOpen !== true) {
        this.groupedPanelSource.next({ isOpen: true, data });
        didOpen = true;
      }
    } else {
      console.error(`openPanel() : The supplied panel type of '${panel}' isn't supported.`);
    }

    if (!didOpen) {
      return;
    }

    console.log('openPanel:', panel, wasArrangementDetailOpen, moment.now());

    if (
      wasArrangementDetailOpen &&
      panel !== PanelType.ArrangementDetail &&
      panel !== PanelType.ArrangementMenu
    ) {
      this.arrangementBlockedSource.next(true);
    }

    // Locked synchronously, NOT inside a setTimeout. closePanel() decrements
    // synchronously and every unlock guards on `if (depth === 0) return`, so a deferred
    // increment could be preceded by its own close - the decrement was swallowed, then the
    // increment landed with nothing to release it and the counter stranded above zero.
    // Close-then-open in one tick is a real flow here (document-library.component.ts:151-160
    // closes and reopens the SAME SendEmail panel, hitting all three counters).
    //
    // This matters much more now that bodyLock also locks the root element: a stranded
    // counter used to leave a harmless overflow:hidden on <body>, but would now freeze the
    // entire page until reload. The 100ms delay also left a window in which a fast
    // tap-and-drag could begin an unlockable document pan, since iOS owns a gesture for its
    // whole duration once begun.
    if (shouldLockBody) {
      this.scrollLockService.bodyLock();
    }

    if (shouldLockArrangement) {
      this.scrollLockService.arrangementLock();
    }

    if (shouldLockInvoice) {
      this.scrollLockService.invoiceHistoryLock();
    }
  }

  closePanel(panel: PanelType) {
    this.preventScrollSource.next(false);
    let didClose = false;
    // Mirrors shouldLockBody's MainMenu exemption exactly - see openPanel.
    const shouldUnlockBody =
      this.sitePanels.includes(panel) && panel !== PanelType.MainMenu;
    const shouldUnlockArrangement = this.arrangementPanels.includes(panel);
    const shouldUnlockInvoice = this.invoicePanels.includes(panel);

    if (panel === PanelType.Message) {
      if (this.messagePanelSource.getValue() !== false) {
        this.messagePanelSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.Notification) {
      if (this.notificationPanelSource.getValue() !== false) {
        this.notificationPanelSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.Profile) {
      if (this.profilePanelSource.getValue() !== false) {
        this.profilePanelSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.DataFilter) {
      if (this.dataFilterPanelSource.getValue().isOpen !== false) {
        this.dataFilterPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.Contacts) {
      if (this.contactPanelSource.getValue().isOpen !== false) {
        this.contactPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.Services) {
      if (this.servicePanelSource.getValue().isOpen !== false) {
        this.servicePanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.Timeline) {
      if (this.timelinePanelSource.getValue().isOpen !== false) {
        this.timelinePanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.EventsTimeline) {
      if (this.eventsTimelinePanelSource.getValue().isOpen !== false) {
        this.eventsTimelinePanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementDetail) {
      if (this.arrangementDetailPanelSource.getValue().isOpen !== false) {
        this.arrangementDetailPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ServiceProviderDetail) {
      if (this.serviceProviderDetailPanelSource.getValue().isOpen !== false) {
        this.serviceProviderDetailPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ProductDetail) {
      if (this.productDetailPanelSource.getValue().isOpen !== false) {
        this.productDetailPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementNote) {
      if (this.arrangementNotePanelSource.getValue().isOpen !== false) {
        this.arrangementNotePanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementTask) {
      if (this.arrangementTaskPanelSource.getValue().isOpen !== false) {
        this.arrangementTaskPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementTasks) {
      if (this.arrangementTasksPanelSource.getValue().isOpen !== false) {
        this.arrangementTasksPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.SendEmail) {
      if (this.sendEmailPanelSource.getValue().isOpen !== false) {
        this.sendEmailPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.MainMenu) {
      if (this.mainMenuSource.getValue() !== false) {
        this.mainMenuSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.MainNavigationMenu) {
      if (this.mainNavigationMenuSource.getValue() !== false) {
        this.mainNavigationMenuSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementMenu) {
      if (this.arrangementMenuPanelSource.getValue() !== false) {
        this.arrangementMenuPanelSource.next(false);
        didClose = true;
      }
    } else if (panel === PanelType.EmailTrails) {
      if (this.emailTrailsPanelSource.getValue().isOpen !== false) {
        this.emailTrailsPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.DocumentLibrary) {
      if (this.documentLibraryPanelSource.getValue().isOpen !== false) {
        this.documentLibraryPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementContact) {
      if (this.arrangementContactPanelSource.getValue().isOpen !== false) {
        this.arrangementContactPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementEvent) {
      if (this.arrangementEventPanelSource.getValue().isOpen !== false) {
        this.arrangementEventPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementTransfer) {
      if (this.arrangementTransferPanelSource.getValue().isOpen !== false) {
        this.arrangementTransferPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementTransfers) {
      if (this.arrangementTransfersPanelSource.getValue().isOpen !== false) {
        this.arrangementTransfersPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.ArrangementInvoiceHistories) {
      if (this.invoiceHistoriesPanelSource.getValue().isOpen !== false) {
        this.invoiceHistoriesPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else if (panel === PanelType.GroupedPanel) {
      if (this.groupedPanelSource.getValue().isOpen !== false) {
        this.groupedPanelSource.next({ isOpen: false, data: null });
        didClose = true;
      }
    } else {
      console.error(`closePanel() : The supplied panel type of '${panel}' isn't supported.`);
    }

    if (panel === PanelType.ArrangementDetail) {
      this.arrangementDetailOpenSource.next(false);
      this.arrangementBlockedSource.next(false);
      this.pruneAnimationDisabledNodes();
    } else if (this.arrangementDetailOpenSource.getValue()) {
      this.arrangementBlockedSource.next(false);
    }

    if (!didClose) {
      return;
    }

    if (shouldUnlockBody) {
      this.scrollLockService.bodyUnlock();
    }

    if (shouldUnlockArrangement) {
      this.scrollLockService.arrangementUnlock();
    }

    if (shouldUnlockInvoice) {
      this.scrollLockService.invoiceHistoryUnlock();
    }
  }

  updateUnreadMessageCount(unreadMessageCount: UnreadMessageCount) {
    this.unreadMessageCountSource.next(unreadMessageCount);
  }

  getUnreadMessageCount(): UnreadMessageCount {
    return this.unreadMessageCountSource.value;
  }

  // The arranger's explicit arrangement-menu preference survives across arrangements
  // and reloads: close it in arrangement A and it stays closed opening arrangement B.
  // Only the explicit header toggle writes it - programmatic opens/closes (breakpoint
  // flips, the detail-panel-open default, the small-screen item-click auto-close) are
  // not preferences. No stored value means default-open.
  arrangementMenuPreferredOpen(): boolean {
    try {
      return localStorage.getItem('arrangementMenuOpen') !== 'closed';
    } catch {
      return true;
    }
  }

  storeArrangementMenuPreference(open: boolean): void {
    try {
      localStorage.setItem('arrangementMenuOpen', open ? 'open' : 'closed');
    } catch {
      // localStorage unavailable - the menu just falls back to default-open.
    }
  }

  // PrimeNG inline calendars (date-v2's picker) bind [@.disabled] on their overlay div,
  // which registers the element in Angular's TransitionAnimationEngine.disabledNodes Set.
  // Angular 12 only removes an entry when that element itself passes through the
  // leave-animation path - tearing down a whole panel subtree at once leaves every entry
  // behind, each retaining its full calendar view tree via __ngContext__ (measured
  // 2026-09-03: ~21 month grids after five open/close cycles). Sweep entries whose
  // element has left the document, one tick later so the removal has happened.
  //
  // Called from date-v2's ngOnDestroy (the ONLY inline-calendar host, so that hook
  // fires at every leak site - including the payment/note dialogs - at the right
  // moment) and, as a backstop, from ArrangementDetail close. Review note: the
  // close-path call usually runs BEFORE the subtree is destroyed (the *ngIf
  // collapses in the unlock() HTTP callback), so it mostly reclaims the PREVIOUS
  // cycle's entries - the date-v2 hook is the properly-timed one. Every internal
  // access is guarded: if an Angular upgrade reshapes these private fields, this
  // silently becomes a no-op instead of breaking.
  pruneAnimationDisabledNodes(): void {
    setTimeout(() => {
      const factory = this.rendererFactory as any;
      const disabledNodes = factory?.engine?._transitionEngine?.disabledNodes;

      if (!disabledNodes || typeof disabledNodes.forEach !== 'function') {
        return;
      }

      disabledNodes.forEach((node: Node) => {
        if (node && !document.contains(node)) {
          disabledNodes.delete(node);
        }
      });
    });
  }

  // The subscription-log debug facility that used to live here (addSubscriptionLog /
  // setSubscriptionLogFinalised / storeSubscriptionLog feeding a Settings "Data Log"
  // panel) was removed 2026-09. It cost a moment().format + arrangement form traversals
  // per HTTP call, an O(n) find per finalise, and a JSON round-trip of up to 10,000
  // localStorage entries on every arrangement close - and its export-via-user workflow
  // was never actually used. The no-op methods remain so the ~400 call sites (mostly
  // finalize() pipes) don't need touching; delete them together with the call sites if
  // that mechanical cleanup ever happens.
  addSubscriptionLog(_that: any, _key: string): void {}

  setSubscriptionLogFinalised(_key: string): void {}

  storeSubscriptionLog(): void {}
}
